import React, { forwardRef, useEffect, useRef, useState } from "react";

/**
 * Reveal — fades + lifts its children in once when scrolled into view.
 *
 * Uses IntersectionObserver with a single-shot disconnect so revealed
 * content stays visible on scroll back. Respects prefers-reduced-motion:
 * users with that setting skip the transform/opacity entirely.
 *
 * Props:
 *  - delay  number  ms to wait before starting (used to stagger siblings)
 *  - as     string  element tag, defaults to "div"
 *  - className string  passthrough for layout classes on the wrapper
 *  - rootMargin string  IO rootMargin override (default "0px 0px -10% 0px")
 *
 * Forwards refs so callers (e.g. scroll-to-section) get the underlying node.
 */
const Reveal = forwardRef(function Reveal(
  {
    children,
    delay = 0,
    as: Tag = "div",
    className = "",
    rootMargin = "0px 0px -10% 0px",
    ...rest
  },
  forwardedRef
) {
  const innerRef = useRef(null);
  const [shown, setShown] = useState(false);

  // Merge the caller's ref with our internal observer ref.
  const setRefs = (node) => {
    innerRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  useEffect(() => {
    const node = innerRef.current;
    if (!node) return;

    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setShown(true);
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.08, rootMargin }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [rootMargin]);

  return (
    <Tag
      ref={setRefs}
      className={`rc-reveal ${shown ? "rc-reveal-in" : ""} ${className}`.trim()}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
});

export default Reveal;
